// Sales Comparison report — current full calendar month/quarter/year
// vs the immediately prior one across revenue / order count / avg
// order value. Server defines the windows; client just picks
// granularity — or your own dates vs the same number of days just before
// (Stephanie 2026-10-16). Below the boxes: the invoices and orders behind
// each period.
import { useEffect, useState } from "react";
import { ReportRowsTable, type ReportList } from "@/components/ReportRowsTable";
import { validCustomRange } from "@/components/ReportShell";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

type Granularity = "month" | "quarter" | "year" | "custom";

interface PeriodSnapshot {
  range: { from: string; to: string };
  revenue: string;
  orderCount: number;
  avgOrderValue: string;
}

interface MetricDelta {
  changePercent: string | null;
  direction: "up" | "down" | "flat";
}

interface Report {
  granularity: Granularity;
  current: PeriodSnapshot;
  prior: PeriodSnapshot;
  deltas: {
    revenue: MetricDelta;
    orderCount: MetricDelta;
    avgOrderValue: MetricDelta;
  };
  fetchedAt: string;
}

const GRANULARITY_LABELS: Record<Granularity, string> = {
  month: "This month vs last",
  quarter: "This quarter vs last",
  year: "This year vs last",
  custom: "Your own dates",
};

const shortRange = (r: { from: string; to: string }) => `${r.from} → ${r.to}`;

function formatCurrency(amount: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function ReportSalesComparisonPage() {
  const [granularity, setGranularity] = useState<Granularity>("month");
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [draft, setDraft] = useState({ from: "", to: "" });
  const [period, setPeriod] = useState<"current" | "prior">("current");
  const [list, setList] = useState<ReportList>("invoices");
  const asked = granularity === "custom" ? custom : null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["reports", "sales-comparison", granularity, asked?.from, asked?.to],
    queryFn: () =>
      api<Report>(
        `/reports/sales-comparison?granularity=${granularity}${asked ? `&from=${asked.from}&to=${asked.to}` : ""}`,
      ),
    enabled: granularity !== "custom" || asked !== null,
  });
  // The custom boxes start on the period being looked at.
  useEffect(() => {
    if (data && draft.from === "") setDraft(data.current.range);
  }, [data, draft.from]);
  const shownRange = data ? data[period].range : null;

  return (
    <div className="mx-auto max-w-6xl">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports">
          <ArrowLeft className="h-4 w-4" /> All reports
        </Link>
      </Button>

      <div className="mb-6">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Reports
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Sales Comparison
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-brown-400">
          Full-period side-by-side — revenue, orders, and avg order value
          against the immediately prior period.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Period
        </span>
        {(Object.keys(GRANULARITY_LABELS) as Granularity[]).map((g) => (
          <button
            key={g}
            type="button"
            onClick={() => {
              if (g === "custom" && custom === null && data) setCustom(data.current.range);
              setGranularity(g);
            }}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide transition-colors",
              granularity === g
                ? "bg-brown-500 text-cream-50"
                : "bg-cream-100 text-brown-400 hover:bg-cream-200",
            )}
          >
            {GRANULARITY_LABELS[g]}
          </button>
        ))}
        {granularity === "custom" && (
          <form
            className="ml-2 flex flex-wrap items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (validCustomRange(draft.from, draft.to)) setCustom(draft);
            }}
          >
            <input type="date" aria-label="From date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} className="h-9 rounded-md border border-rule bg-white px-2 text-sm" />
            <span className="text-xs text-brown-300">to</span>
            <input type="date" aria-label="To date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} className="h-9 rounded-md border border-rule bg-white px-2 text-sm" />
            <Button type="submit" size="sm" disabled={!validCustomRange(draft.from, draft.to)}>Compare</Button>
          </form>
        )}
      </div>
      {data && (
        <p className="-mt-3 mb-6 text-xs text-brown-300">
          {shortRange(data.current.range)} compared with {shortRange(data.prior.range)}
        </p>
      )}

      {isLoading ? (
        <div className="rounded-lg border border-brown-100 bg-white p-10 text-center text-sm text-brown-300">
          Loading…
        </div>
      ) : isError || !data ? (
        <div className="rounded-lg border border-rust-300 bg-rust-50 p-10 text-center text-sm text-rust-600">
          Couldn't load report.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <ComparisonTile
            label="Revenue"
            priorValue={formatCurrency(data.prior.revenue)}
            currentValue={formatCurrency(data.current.revenue)}
            delta={data.deltas.revenue}
          />
          <ComparisonTile
            label="Orders"
            priorValue={data.prior.orderCount.toLocaleString()}
            currentValue={data.current.orderCount.toLocaleString()}
            delta={data.deltas.orderCount}
          />
          <ComparisonTile
            label="Avg order value"
            priorValue={formatCurrency(data.prior.avgOrderValue)}
            currentValue={formatCurrency(data.current.avgOrderValue)}
            delta={data.deltas.avgOrderValue}
          />
        </div>
      )}

      {data && shownRange && (
        <>
          <div className="mt-8 flex flex-wrap items-center gap-2">
            {(["current", "prior"] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPeriod(p)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
                  period === p ? "bg-brown-500 text-cream-50" : "bg-cream-100 text-brown-400 hover:bg-cream-200",
                )}
              >
                {p === "current" ? "This period" : "Prior period"} · {shortRange(data[p].range)}
              </button>
            ))}
            <span className="mx-2 h-4 w-px bg-brown-100" />
            {([
              ["invoices", "Invoices (revenue)"],
              ["sales_orders", "Orders"],
            ] as const).map(([l, label]) => (
              <button
                key={l}
                type="button"
                onClick={() => setList(l)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
                  list === l ? "bg-brown-500 text-cream-50" : "bg-cream-100 text-brown-400 hover:bg-cream-200",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <ReportRowsTable
            list={list}
            range={shownRange}
            title={`${list === "invoices" ? "Invoices" : "Orders"} · ${period === "current" ? "this period" : "prior period"}`}
          />
        </>
      )}

      {data?.fetchedAt && (
        <p className="mt-8 text-xs text-brown-300">
          As of {new Date(data.fetchedAt).toLocaleString()}
        </p>
      )}
    </div>
  );
}

function ComparisonTile({
  label,
  priorValue,
  currentValue,
  delta,
}: {
  label: string;
  priorValue: string;
  currentValue: string;
  delta: MetricDelta;
}) {
  return (
    <div className="flex flex-col rounded-lg border border-brown-100 bg-white p-5">
      <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        {label}
      </div>
      <p className="mt-3 text-xs text-brown-400">
        Prior:{" "}
        <span className="font-semibold text-brown-500">{priorValue}</span>
      </p>
      <div className="mt-2 flex items-baseline justify-between gap-2">
        <span className="font-display text-3xl font-semibold text-brown-500">
          {currentValue}
        </span>
        <DeltaBadge delta={delta} />
      </div>
    </div>
  );
}

function DeltaBadge({ delta }: { delta: MetricDelta }) {
  if (delta.changePercent === null) {
    return (
      <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
        N/A
      </span>
    );
  }
  const cls =
    delta.direction === "up"
      ? "bg-peach-100 text-brown-600"
      : delta.direction === "down"
        ? "bg-rust-100 text-rust-700"
        : "text-brown-300";
  const arrow =
    delta.direction === "up" ? "↑" : delta.direction === "down" ? "↓" : "→";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
        cls,
      )}
    >
      {arrow} {delta.changePercent}%
    </span>
  );
}
